import { useState, useEffect } from 'react';
import { IndianRupee, TrendingUp, Users, User, Tag, Ticket } from 'lucide-react';
import { adminApi } from '../services/api';
import toast from 'react-hot-toast';

const formatINR = (amount) => {
  if (amount == null || isNaN(amount)) return '₹0';
  return '₹' + Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const periodLabel = (period, view) => {
  if (view === 'annual') return period;
  if (view === 'quarterly') return period; // e.g. "2026-Q1"
  // monthly: "2026-02" → "Feb 2026"
  const [y, m] = period.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(m) - 1]} ${y}`;
};

export default function RevenueTab() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [view, setView] = useState('monthly');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [year, view]);

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await adminApi.getRevenueAnalytics(year, view);
      setData(result);
    } catch (error) {
      console.error('Failed to load revenue analytics:', error);
      toast.error('Failed to load revenue analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!data) return null;

  const { summary, periods, byTrainer, byType, topCoupons } = data;
  const views = ['monthly', 'quarterly', 'annual'];
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear; y >= 2026; y--) years.push(y);

  return (
    <div className="space-y-6">
      {/* Header with controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-lg font-semibold text-gray-900">Revenue Analytics</h2>
        <div className="flex items-center gap-3">
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            {views.map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  view === v
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <SummaryCard
          label="Gross Revenue"
          amount={summary.grossTotal}
          sub={`${summary.transactions} txns`}
          icon={TrendingUp}
          color="blue"
        />
        <SummaryCard
          label="Discounts"
          amount={summary.totalDiscount}
          sub={summary.grossTotal > 0 ? `${Math.round(summary.totalDiscount / summary.grossTotal * 100)}% of gross` : '0%'}
          icon={Ticket}
          color="red"
        />
        <SummaryCard
          label="Net Revenue"
          amount={summary.total}
          sub="Collected"
          icon={IndianRupee}
          color="green"
        />
        <SummaryCard
          label="Platform (45%)"
          amount={summary.platformShare}
          sub={summary.total > 0 ? `${Math.round(summary.platformShare / summary.total * 100)}%` : '0%'}
          icon={IndianRupee}
          color="purple"
        />
        <SummaryCard
          label="Deepak"
          amount={summary.deepakShare}
          sub={summary.total > 0 ? `${Math.round(summary.deepakShare / summary.total * 100)}%` : '0%'}
          icon={User}
          color="amber"
        />
      </div>

      {/* Trainer share card (only if non-zero) */}
      {summary.trainerShare > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-orange-600" />
            <span className="font-medium text-orange-800">Trainer Commissions: {formatINR(summary.trainerShare)}</span>
            <span className="text-sm text-orange-600">({summary.total > 0 ? Math.round(summary.trainerShare / summary.total * 100) : 0}% of net)</span>
          </div>
        </div>
      )}

      {/* Period Breakdown Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="font-medium text-gray-900">Period Breakdown</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-gray-500">
                <th className="px-4 py-2 font-medium">Period</th>
                <th className="px-4 py-2 font-medium text-right">Gross</th>
                <th className="px-4 py-2 font-medium text-right">Discount</th>
                <th className="px-4 py-2 font-medium text-right">Net</th>
                <th className="px-4 py-2 font-medium text-right">Platform</th>
                <th className="px-4 py-2 font-medium text-right">Trainers</th>
                <th className="px-4 py-2 font-medium text-right">Deepak</th>
                <th className="px-4 py-2 font-medium text-right">Txns</th>
              </tr>
            </thead>
            <tbody>
              {[...periods].reverse().map((p, i) => (
                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium text-gray-900">{periodLabel(p.period, view)}</td>
                  <td className="px-4 py-2 text-right text-gray-600">{formatINR(p.grossTotal)}</td>
                  <td className="px-4 py-2 text-right text-red-600">{p.discount > 0 ? `-${formatINR(p.discount)}` : '-'}</td>
                  <td className="px-4 py-2 text-right font-medium text-gray-900">{formatINR(p.netTotal)}</td>
                  <td className="px-4 py-2 text-right text-purple-600">{formatINR(p.platformShare)}</td>
                  <td className="px-4 py-2 text-right text-orange-600">{formatINR(p.trainerShare)}</td>
                  <td className="px-4 py-2 text-right text-amber-600">{formatINR(p.deepakShare)}</td>
                  <td className="px-4 py-2 text-right text-gray-500">{p.transactions}</td>
                </tr>
              ))}
              {periods.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No transactions this year</td></tr>
              )}
            </tbody>
            {periods.length > 1 && (
              <tfoot>
                <tr className="bg-gray-50 font-medium">
                  <td className="px-4 py-2">Total</td>
                  <td className="px-4 py-2 text-right">{formatINR(summary.grossTotal)}</td>
                  <td className="px-4 py-2 text-right text-red-600">{summary.totalDiscount > 0 ? `-${formatINR(summary.totalDiscount)}` : '-'}</td>
                  <td className="px-4 py-2 text-right">{formatINR(summary.total)}</td>
                  <td className="px-4 py-2 text-right text-purple-600">{formatINR(summary.platformShare)}</td>
                  <td className="px-4 py-2 text-right text-orange-600">{formatINR(summary.trainerShare)}</td>
                  <td className="px-4 py-2 text-right text-amber-600">{formatINR(summary.deepakShare)}</td>
                  <td className="px-4 py-2 text-right">{summary.transactions}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Bottom sections: By Trainer + By Payment Type + Top Coupons */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* By Trainer */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <Users className="w-4 h-4" /> By Trainer
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-gray-500">
                  <th className="px-4 py-2 font-medium">Trainer</th>
                  <th className="px-4 py-2 font-medium text-right">Revenue</th>
                  <th className="px-4 py-2 font-medium text-right">Commission</th>
                  <th className="px-4 py-2 font-medium text-right">Txns</th>
                </tr>
              </thead>
              <tbody>
                {byTrainer.map((t, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-900">{t.trainerName}</td>
                    <td className="px-4 py-2 text-right font-medium">{formatINR(t.total)}</td>
                    <td className="px-4 py-2 text-right text-orange-600">{formatINR(t.commission)}</td>
                    <td className="px-4 py-2 text-right text-gray-500">{t.transactions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* By Payment Type */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <Tag className="w-4 h-4" /> By Payment Type
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-gray-500">
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium text-right">Net</th>
                  <th className="px-4 py-2 font-medium text-right">Discount</th>
                  <th className="px-4 py-2 font-medium text-right">Txns</th>
                </tr>
              </thead>
              <tbody>
                {byType.map((t, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-900">{t.type}</td>
                    <td className="px-4 py-2 text-right font-medium">{formatINR(t.total)}</td>
                    <td className="px-4 py-2 text-right text-red-600">{t.discount > 0 ? formatINR(t.discount) : '-'}</td>
                    <td className="px-4 py-2 text-right text-gray-500">{t.transactions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Coupons */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <Ticket className="w-4 h-4" /> Coupon Usage
            </h3>
          </div>
          {topCoupons.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-400 text-sm">No coupons used this year</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-gray-500">
                    <th className="px-4 py-2 font-medium">Code</th>
                    <th className="px-4 py-2 font-medium text-right">Uses</th>
                    <th className="px-4 py-2 font-medium text-right">Total Discount</th>
                  </tr>
                </thead>
                <tbody>
                  {topCoupons.map((c, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">{c.code}</span>
                        <span className="text-gray-400 text-xs ml-2">
                          {c.discountType === 'percentage' ? `${c.discountValue}%` : `₹${c.discountValue}`}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right text-gray-600">{c.timesUsed}</td>
                      <td className="px-4 py-2 text-right text-red-600 font-medium">{formatINR(c.totalDiscount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, amount, sub, icon: Icon, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    amber: 'bg-amber-50 text-amber-600 border-amber-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    orange: 'bg-orange-50 text-orange-600 border-orange-200',
  };

  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 opacity-70" />
        <span className="text-xs font-medium opacity-70">{label}</span>
      </div>
      <div className="text-xl font-bold">{formatINR(amount)}</div>
      <div className="text-xs opacity-60 mt-0.5">{sub}</div>
    </div>
  );
}
